import React, { useState, useEffect } from 'react';
import { promotionsAPI, productsAPI } from '../services/api';
import Swal from 'sweetalert2';
import io from 'socket.io-client';
import { SOCKET_URL, BASE_URL } from '../config/socket';

const socket = io(SOCKET_URL);

function Promotions() {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'percentage',
    discount_value: 0,
    discount_percentage: 0,
    buy_quantity: 1,
    get_quantity: 1,
    product_id: '',
    min_purchase_amount: 0,
    start_date: '',
    end_date: '',
    no_expiry: false,
    status: 'active',
    member_only: false,
    package_price: 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchPromotions();
    fetchProducts();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await promotionsAPI.getAll();
      setPromotions(response.data);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ status: 'active' });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Prepare data based on promotion type
      const submitData = {
        ...formData,
        start_date: formData.no_expiry ? null : (formData.start_date || null),
        end_date: formData.no_expiry ? null : (formData.end_date || null),
      };

      // Clear fields not needed for this promotion type
      switch (formData.type) {
        case 'percentage':
          submitData.discount_value = 0;
          submitData.buy_quantity = 0;
          submitData.get_quantity = 0;
          submitData.package_price = 0;
          break;
        case 'fixed_amount':
          submitData.discount_percentage = 0;
          submitData.buy_quantity = 0;
          submitData.get_quantity = 0;
          submitData.package_price = 0;
          break;
        case 'buy_x_get_y':
          submitData.discount_value = 0;
          submitData.discount_percentage = 0;
          submitData.package_price = 0;
          break;
        case 'package':
          submitData.discount_value = 0;
          submitData.discount_percentage = 0;
          submitData.get_quantity = 0;
          submitData.min_purchase_amount = 0;
          break;
        default:
          break;
      }

      let promotionId;

      if (editingPromotion) {
        await promotionsAPI.update(editingPromotion.id, submitData);
        promotionId = editingPromotion.id;
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ!',
          text: 'อัปเดตโปรโมชั่นเรียบร้อยแล้ว',
          confirmButtonColor: '#0ea5e9'
        });
      } else {
        const response = await promotionsAPI.create(submitData);
        promotionId = response.data.id;
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ!',
          text: 'สร้างโปรโมชั่นเรียบร้อยแล้ว',
          confirmButtonColor: '#0ea5e9'
        });
      }

      // Upload image if selected
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        await promotionsAPI.uploadImage(promotionId, imageFormData);
      }

      // Notify customer display
      socket.emit('update-promotion', { promotionId });

      setShowModal(false);
      setEditingPromotion(null);
      resetForm();
      fetchPromotions();
    } catch (error) {
      console.error('Error saving promotion:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.error || 'ไม่สามารถบันทึกโปรโมชั่นได้',
        confirmButtonColor: '#0ea5e9'
      });
    }
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      name: promotion.name,
      description: promotion.description || '',
      type: promotion.type,
      discount_value: promotion.discount_value || 0,
      discount_percentage: promotion.discount_percentage || 0,
      buy_quantity: promotion.buy_quantity || 1,
      get_quantity: promotion.get_quantity || 1,
      product_id: promotion.product_id || '',
      min_purchase_amount: promotion.min_purchase_amount || 0,
      start_date: promotion.start_date ? promotion.start_date.slice(0, 16) : '',
      end_date: promotion.end_date ? promotion.end_date.slice(0, 16) : '',
      no_expiry: !promotion.start_date && !promotion.end_date,
      status: promotion.status,
      member_only: promotion.member_only || false,
      package_price: promotion.package_price || 0,
    });
    if (promotion.image) {
      setImagePreview(`${BASE_URL.replace('/api', '')}${promotion.image}`);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณต้องการลบโปรโมชั่นนี้ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก'
    });

    if (result.isConfirmed) {
      try {
        await promotionsAPI.delete(id);
        Swal.fire({
          icon: 'success',
          title: 'ลบสำเร็จ!',
          text: 'ลบโปรโมชั่นเรียบร้อยแล้ว',
          confirmButtonColor: '#0ea5e9'
        });
        fetchPromotions();
        socket.emit('update-promotion', {});
      } catch (error) {
        console.error('Error deleting promotion:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถลบโปรโมชั่นได้',
          confirmButtonColor: '#0ea5e9'
        });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'percentage',
      discount_value: 0,
      discount_percentage: 0,
      buy_quantity: 1,
      get_quantity: 1,
      product_id: '',
      min_purchase_amount: 0,
      start_date: '',
      end_date: '',
      no_expiry: false,
      status: 'active',
      member_only: false,
      package_price: 0,
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const getPromotionTypeLabel = (type) => {
    switch (type) {
      case 'percentage': return 'ส่วนลดเปอร์เซ็นต์';
      case 'fixed_amount': return 'ส่วนลดจำนวนเงิน';
      case 'buy_x_get_y': return 'ซื้อ X แถม Y';
      case 'package': return 'แพ็คเกจ';
      default: return type;
    }
  };

  const getPromotionDetails = (promotion) => {
    switch (promotion.type) {
      case 'percentage':
        return `ลด ${promotion.discount_percentage}%`;
      case 'fixed_amount':
        return `ลด ${promotion.discount_value} บาท`;
      case 'buy_x_get_y':
        return `ซื้อ ${promotion.buy_quantity} แถม ${promotion.get_quantity}`;
      case 'package':
        return `${promotion.buy_quantity} ชิ้น = ${promotion.package_price} บาท`;
      default:
        return '';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">จัดการโปรโมชั่น</h2>
        <button
          onClick={() => {
            setEditingPromotion(null);
            resetForm();
            setShowModal(true);
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-semibold"
        >
          + เพิ่มโปรโมชั่น
        </button>
      </div>

      {/* Promotions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {promotions.map((promotion) => (
          <div
            key={promotion.id}
            className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-gray-200 hover:border-primary-400 transition-all"
          >
            {/* Image */}
            {promotion.image && (
              <img
                src={`${BASE_URL.replace('/api', '')}${promotion.image}`}
                alt={promotion.name}
                className="w-full h-48 object-cover"
              />
            )}

            {/* Content */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-bold text-gray-800 flex-1">{promotion.name}</h3>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  promotion.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {promotion.status === 'active' ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                </span>
              </div>

              <div className="space-y-1 text-sm text-gray-600 mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">ประเภท:</span>
                  <span className="text-primary-600 font-medium">{getPromotionTypeLabel(promotion.type)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">รายละเอียด:</span>
                  <span className="text-orange-600 font-medium">{getPromotionDetails(promotion)}</span>
                </div>
                {promotion.product_name && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">สินค้า:</span>
                    <span>{promotion.product_name}</span>
                  </div>
                )}
                {promotion.min_purchase_amount > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">ยอดขั้นต่ำ:</span>
                    <span>฿{promotion.min_purchase_amount}</span>
                  </div>
                )}
                {promotion.member_only && (
                  <div className="text-purple-600 font-medium">👤 เฉพาะสมาชิก</div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t">
                <button
                  onClick={() => handleEdit(promotion)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded font-semibold"
                >
                  แก้ไข
                </button>
                <button
                  onClick={() => handleDelete(promotion.id)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded font-semibold"
                >
                  ลบ
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                {editingPromotion ? 'แก้ไขโปรโมชั่น' : 'เพิ่มโปรโมชั่น'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingPromotion(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-semibold mb-1">ชื่อโปรโมชั่น *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">คำอธิบาย</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-2 border rounded"
                  rows="2"
                />
              </div>

              {/* Promotion Type */}
              <div>
                <label className="block text-sm font-semibold mb-1">ประเภทโปรโมชั่น *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full p-2 border rounded"
                  required
                >
                  <option value="percentage">ส่วนลดเปอร์เซ็นต์</option>
                  <option value="fixed_amount">ส่วนลดจำนวนเงิน</option>
                  <option value="buy_x_get_y">ซื้อ X แถม Y</option>
                  <option value="package">แพ็คเกจ</option>
                </select>
              </div>

              {/* Type-specific fields */}
              {formData.type === 'percentage' && (
                <div>
                  <label className="block text-sm font-semibold mb-1">เปอร์เซ็นต์ส่วนลด (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: parseFloat(e.target.value) })}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
              )}

              {formData.type === 'fixed_amount' && (
                <div>
                  <label className="block text-sm font-semibold mb-1">จำนวนส่วนลด (บาท) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
              )}

              {formData.type === 'buy_x_get_y' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">จำนวนที่ซื้อ *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.buy_quantity}
                      onChange={(e) => setFormData({ ...formData, buy_quantity: parseInt(e.target.value) })}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">จำนวนที่แถม *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.get_quantity}
                      onChange={(e) => setFormData({ ...formData, get_quantity: parseInt(e.target.value) })}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                </div>
              )}

              {formData.type === 'package' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">จำนวนชิ้น *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.buy_quantity}
                      onChange={(e) => setFormData({ ...formData, buy_quantity: parseInt(e.target.value) })}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">ราคาแพ็คเกจ (บาท) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.package_price}
                      onChange={(e) => setFormData({ ...formData, package_price: parseFloat(e.target.value) })}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Product Selection */}
              <div>
                <label className="block text-sm font-semibold mb-1">
                  สินค้า {formData.type === 'buy_x_get_y' || formData.type === 'package' ? '*' : '(ทุกสินค้า ถ้าไม่ระบุ)'}
                </label>
                <select
                  value={formData.product_id}
                  onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                  className="w-full p-2 border rounded"
                  required={formData.type === 'buy_x_get_y' || formData.type === 'package'}
                >
                  <option value="">-- ทุกสินค้า --</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} (฿{product.selling_price})
                    </option>
                  ))}
                </select>
              </div>

              {/* Minimum Purchase */}
              {formData.type !== 'package' && (
                <div>
                  <label className="block text-sm font-semibold mb-1">ยอดซื้อขั้นต่ำ (บาท)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.min_purchase_amount}
                    onChange={(e) => setFormData({ ...formData, min_purchase_amount: parseFloat(e.target.value) })}
                    className="w-full p-2 border rounded"
                  />
                </div>
              )}

              {/* Date Range */}
              <div>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={formData.no_expiry}
                    onChange={(e) => setFormData({ ...formData, no_expiry: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-semibold">ไม่มีวันหมดอายุ</span>
                </label>
              </div>

              {!formData.no_expiry && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">วันที่เริ่ม</label>
                    <input
                      type="datetime-local"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">วันที่สิ้นสุด</label>
                    <input
                      type="datetime-local"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                </div>
              )}

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold mb-1">สถานะ</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full p-2 border rounded"
                >
                  <option value="active">ใช้งาน</option>
                  <option value="inactive">ไม่ใช้งาน</option>
                </select>
              </div>

              {/* Member Only */}
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.member_only}
                    onChange={(e) => setFormData({ ...formData, member_only: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-semibold">เฉพาะสมาชิก</span>
                </label>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold mb-1">
                  รูปภาพโปรโมชั่น
                  <span className="text-gray-500 font-normal ml-2">(ขนาดไหนก็ได้)</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full p-2 border rounded"
                />
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="mt-2 max-h-48 rounded border" />
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded font-semibold"
                >
                  {editingPromotion ? 'บันทึก' : 'เพิ่มโปรโมชั่น'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPromotion(null);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded font-semibold"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Promotions;
